import filesize from "filesize";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import type { FileViolation } from "../../../../shared/core/data-forms/file-constraints";
import type { FileProps, FormImageInput } from "../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../shared/core/logging/logger";
import type { UploadedImage } from "../../../../shared/utils/images-converter";
import { FileUploader, FileUploaderType, MATCH_FILE_UPLOADER_TYPES } from "../forms/file-uploader";

interface ImageDimension {
  width: number;
  height: number;
}

export const SingleImageField: React.FC<{
  input: FormImageInput;
  fileProps?: FileProps;
  value?: string;
  index?: number;
  hasExistingFile?: boolean;
  disabled?: boolean;
  onChange: (image: UploadedImage | null) => void;
  onViolations?: (violations: FileViolation[]) => void;
}> = ({ input, value, index, disabled, hasExistingFile, fileProps, onChange, onViolations }) => {
  const { formatMessage } = useIntl();
  const [previewDisplayed, setPreviewDisplayed] = useState(false);
  const [image, setImage] = useState<UploadedImage | null>(null);

  useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        setImage(value.startsWith("data:") ? value : `data:image/png;base64,${value}`);
      } else if (typeof value === "object") {
        setImage(null);
      }
    } else {
      setImage(null);
    }
    setPreviewDisplayed(false);
  }, [value, index]);

  let acceptedTypes: FileUploaderType[];
  if (input.acceptedTypes) {
    const inputTypes = input.acceptedTypes;
    acceptedTypes = inputTypes.map((item) => MATCH_FILE_UPLOADER_TYPES[item] ?? item);
    acceptedTypes = acceptedTypes.filter((item) => ![FileUploaderType.PDF, FileUploaderType.DOCX].includes(item));
  } else {
    acceptedTypes = [FileUploaderType.PNG, FileUploaderType.JPG, FileUploaderType.JPEG];
  }

  const loadImgDimensions = async (imageUrl: string, setDimensions: (ImageDimension) => void) => {
    const img = new window.Image();
    img.src = imageUrl;
    img.onload = () => setDimensions({ width: img.width, height: img.height });
    img.onerror = (err) => {
      logger.debug("Image dimensions error : " + err);
    };
  };

  const onDelete = () => {
    setPreviewDisplayed(false);
    setImage(null);
    onChange(null);
  };

  return (
    <InputContainer>
      <FileUploader
        hasExistingFile={hasExistingFile}
        initialFile={
          value && hasExistingFile
            ? {
                name: formatMessage("registerScreen.pictureInputFilled"),
                type: FileUploaderType.PNG,
                size: typeof value === "string" ? filesize(value.length) : "0",
              }
            : undefined
        }
        typeAllowed={acceptedTypes}
        fileUploaded={async (file, props) => {
          if (!file) {
            onChange(null);
            return;
          }
          const reader = new FileReader();
          console.log("Uploaded file name:", file.name);
          reader.addEventListener(
            "load",
            async () => {
              const base64Image = reader.result?.toString();
              if (base64Image) {
                const setImageDimension = (imageDimension: ImageDimension) => {
                  onChange({
                    base64: base64Image,
                    width: imageDimension.width,
                    height: imageDimension.height,
                    fileProps: props,
                  });
                };
                await loadImgDimensions(base64Image, setImageDimension);
                setImage(base64Image);
              }
            },
            false,
          );
          reader.readAsDataURL(file);
        }}
        onPreviewClicked={() => setPreviewDisplayed(!previewDisplayed)}
        onImageDeleted={onDelete}
        instructions={formatMessage("registerScreen.pictureInputEmpty")}
        disabled={disabled}
        maxSize={input.maxSize}
        onViolations={onViolations}
        required={input.required}
        badgeIndex={index}
        fileProps={fileProps}
      />
      {previewDisplayed && image ? <PreviewImage src={image} /> : null}
    </InputContainer>
  );
};

const InputContainer = styled.div`
  position: relative;
  flex: 1;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: contain;
`;
